/**
 * parts/landmarks.js — the pieces that give the midway a skyline and its ends.
 *
 * The ferris wheel is the silhouette a player sees from the entrance; the gate
 * is the first thing they walk under; the prize desk is what the whole economy
 * points at. All three get more detail than their distance strictly warrants,
 * because they are what the game looks like in a screenshot.
 *
 * @module assets3d/parts/landmarks
 */

import * as THREE from 'three';
import { ART, familyAt, shade, tint, rngFor } from '../palette.js';
import {
  stripes, woodGrain, starburst, bulbBand, scallopAlpha, buntingStrip, paint, showcardText,
} from '../textures.js';
import { geo, mat, mesh, slab, bulb, finial, createSign } from './core.js';
import { createLampString } from './midway.js';
import { createPlush } from './prizes.js';

/**
 * A background ferris wheel. Eight swinging gondolas on a spoked rim.
 * @param {number} [radius]
 * @returns {THREE.Group} with `userData.spin(dt)`.
 */
export function createFerrisWheel(radius = 9) {
  const g = new THREE.Group();
  g.name = 'ferris-wheel';
  const hubY = radius + 1.5;
  const steel = mat(ART.metal, { rough: 0.45, metal: 0.6 });
  const trim = mat(ART.red, { rough: 0.6 });

  // Everything that turns lives under one group; the gondolas are counter-
  // rotated in spin() so they hang level however far the wheel has come round.
  const wheel = new THREE.Group();
  wheel.name = 'wheel';
  wheel.position.y = hubY;
  g.add(wheel);

  const rim = mesh(new THREE.TorusGeometry(radius, 0.16, 8, 44), steel);
  wheel.add(rim);

  const innerRim = mesh(new THREE.TorusGeometry(radius * 0.30, 0.10, 6, 30), steel);
  wheel.add(innerRim);

  const SPOKES = 8;
  const gondolas = [];
  for (let i = 0; i < SPOKES; i++) {
    const a = (i / SPOKES) * Math.PI * 2;

    const spoke = mesh(
      geo(`fw-spoke-${radius}`, () => new THREE.CylinderGeometry(0.07, 0.07, radius * 2, 6)),
      steel,
    );
    spoke.rotation.z = a;
    wheel.add(spoke);

    // Tension cables read as the fine detail that makes a wheel look engineered.
    const cable = mesh(
      geo(`fw-cable-${radius}`, () => new THREE.CylinderGeometry(0.025, 0.025, radius, 4)),
      steel,
      { receive: false },
    );
    cable.position.set(Math.cos(a + 0.4) * radius * 0.5, Math.sin(a + 0.4) * radius * 0.5, 0.22);
    cable.rotation.z = a + 0.4 + Math.PI / 2;
    wheel.add(cable);

    // Gondola hangs from a pivot pinned on the rim.
    const pivot = new THREE.Group();
    pivot.position.set(Math.cos(a) * radius, Math.sin(a) * radius, 0);
    wheel.add(pivot);
    gondolas.push(pivot);

    const fam = familyAt(i);
    const hanger = mesh(
      geo('fw-hanger', () => new THREE.CylinderGeometry(0.04, 0.04, 0.42, 5)),
      steel,
    );
    hanger.position.y = -0.21;
    pivot.add(hanger);

    const car = mesh(slab(1.15, 0.85, 1.0, 0.06), mat(fam.canopy, { rough: 0.62 }));
    car.position.y = -0.86;
    pivot.add(car);

    const roof = mesh(
      geo('fw-roof', () => new THREE.ConeGeometry(0.92, 0.36, 8)),
      mat(0xffffff, { rough: 0.6, map: stripes(fam.canopy, ART.cream, 8) }),
    );
    roof.position.y = -0.32;
    roof.rotation.y = Math.PI / 8;
    pivot.add(roof);

    const skirt = mesh(slab(1.22, 0.10, 1.07, 0.02), mat(ART.gold, { rough: 0.5 }));
    skirt.position.y = -0.50;
    pivot.add(skirt);
  }

  // Hub, axle and A-frame legs.
  const hubDisc = mesh(
    geo('fw-hub', () => new THREE.CylinderGeometry(0.7, 0.7, 0.7, 14)),
    mat(ART.gold, { rough: 0.4, metal: 0.6 }),
  );
  hubDisc.rotation.x = Math.PI / 2;
  hubDisc.position.y = hubY;
  g.add(hubDisc);

  for (const s of [-1, 1]) {
    for (const z of [-0.9, 0.9]) {
      const leg = mesh(
        geo(`fw-leg-${radius}`, () => new THREE.CylinderGeometry(0.16, 0.30, hubY * 1.06, 8)),
        steel,
      );
      leg.position.set(s * radius * 0.46, hubY * 0.53, z);
      leg.rotation.z = -s * 0.40;
      leg.rotation.x = -z * 0.10;
      g.add(leg);
    }
    const brace = mesh(
      geo(`fw-brace-${radius}`, () => new THREE.CylinderGeometry(0.09, 0.09, radius * 0.9, 5)),
      trim,
      { receive: false },
    );
    brace.position.set(s * radius * 0.26, hubY * 0.42, 0);
    brace.rotation.z = Math.PI / 2;
    g.add(brace);
  }

  const base = mesh(slab(radius * 1.5, 0.4, 3.0, 0.06), mat(ART.plum, { rough: 0.85 }));
  base.position.y = 0.2;
  g.add(base);

  /**
   * Turn the wheel, keeping the gondolas level.
   * @param {number} dt
   */
  g.userData.spin = (dt) => {
    wheel.rotation.z += dt * 0.12;
    for (const pivot of gondolas) pivot.rotation.z = -wheel.rotation.z;
  };
  g.userData.wheel = wheel;
  return g;
}

/**
 * The entrance arch: two towers, a lettered marquee, bulbs and flags.
 * @param {number} [width] @param {number} [height]
 * @returns {THREE.Group} with `userData.lights` (a lamp string).
 */
export function createEntranceGate(width = 9, height = 5.5) {
  const g = new THREE.Group();
  g.name = 'entrance-gate';
  const towerW = 1.05;

  for (const s of [-1, 1]) {
    const x = (width / 2) * s;

    const tower = mesh(
      geo(`gate-tower-${height}`, () => new THREE.BoxGeometry(towerW, height, towerW)),
      mat(0xffffff, { rough: 0.72, map: stripes(ART.red, ART.cream, 8) }),
    );
    tower.position.set(x, height / 2, 0);
    g.add(tower);

    const plinth = mesh(slab(towerW * 1.28, 0.45, towerW * 1.28, 0.04), mat(ART.plum, { rough: 0.85 }));
    plinth.position.set(x, 0.22, 0);
    g.add(plinth);

    const cornice = mesh(slab(towerW * 1.30, 0.26, towerW * 1.30, 0.04), mat(ART.gold, { rough: 0.45, metal: 0.3 }));
    cornice.position.set(x, height + 0.13, 0);
    g.add(cornice);

    // A little onion cap and a flag, so the towers finish rather than stop.
    const cap = mesh(
      geo('gate-cap', () => new THREE.ConeGeometry(towerW * 0.78, 0.85, 8)),
      mat(0xffffff, { rough: 0.6, map: stripes(ART.teal, ART.cream, 8) }),
    );
    cap.position.set(x, height + 0.68, 0);
    cap.rotation.y = Math.PI / 8;
    g.add(cap);

    const knob = finial(0.16, ART.gold);
    knob.position.set(x, height + 1.14, 0);
    g.add(knob);

    const mast = mesh(
      geo('gate-mast', () => new THREE.CylinderGeometry(0.028, 0.028, 0.85, 5)),
      mat(ART.iron, { rough: 0.5, metal: 0.6 }),
    );
    mast.position.set(x, height + 1.6, 0);
    g.add(mast);

    const flag = mesh(
      geo('gate-flag', () => {
        const sh = new THREE.Shape();
        sh.moveTo(0, 0.28);
        sh.lineTo(0.62, 0.12);
        sh.lineTo(0, -0.06);
        sh.closePath();
        const f = new THREE.ShapeGeometry(sh, 1);
        f.rotateY(Math.PI / 2);
        return f;
      }),
      mat(s < 0 ? ART.gold : ART.teal, { rough: 0.7, side: THREE.DoubleSide }),
      { receive: false },
    );
    flag.position.set(x, height + 1.82, 0);
    g.add(flag);
  }

  // ── Marquee ───────────────────────────────────────────────────────────────
  const marqueeY = height + 0.72;
  const marquee = mesh(
    slab(width + towerW + 0.3, 1.45, 0.55, 0.04),
    mat(ART.plumDeep, { rough: 0.82 }),
  );
  marquee.position.y = marqueeY;
  g.add(marquee);

  for (const face of [-1, 1]) {
    const burst = mesh(
      new THREE.PlaneGeometry(width + towerW, 1.1),
      mat(0xffffff, {
        flat: true, map: starburst(ART.red, ART.plumDeep, 20),
        transparent: true, opacity: 0.6,
      }),
      { cast: false, receive: false },
    );
    burst.position.set(0, marqueeY, 0.281 * face);
    if (face < 0) burst.rotation.y = Math.PI;
    g.add(burst);

    for (const dy of [0.62, -0.62]) {
      const band = mesh(
        new THREE.PlaneGeometry(width + towerW + 0.3, 0.16),
        mat(0xffffff, { flat: true, map: bulbBand(Math.round(width * 3)) }),
        { cast: false, receive: false },
      );
      band.position.set(0, marqueeY + dy, 0.282 * face);
      if (face < 0) band.rotation.y = Math.PI;
      g.add(band);
    }
  }

  // Real bulbs picking out the arch, visible from the walkway.
  const dots = Math.max(8, Math.round(width * 1.6));
  for (let i = 0; i < dots; i++) {
    const b = bulb(0.075, ART.bulbHot);
    b.position.set(-width / 2 + (width / (dots - 1)) * i, marqueeY - 0.80, -0.30);
    g.add(b);
  }

  const sign = createSign('CARNIVAL CHAOS', {
    w: width * 0.86, h: 1.0,
    bg: 'transparent', color: `#${ART.gold.toString(16)}`, border: null,
    size: 150, doubleSided: true,
  });
  sign.position.set(0, marqueeY, -0.285);
  sign.rotation.y = Math.PI;
  sign.name = 'gate-sign';
  g.add(sign);

  // Scalloped valance under the marquee.
  const valance = mesh(
    new THREE.PlaneGeometry(width + towerW + 0.3, 0.42),
    mat(0xffffff, {
      rough: 0.8, side: THREE.DoubleSide,
      map: stripes(ART.red, ART.cream, Math.round(width * 2)),
      alphaMap: scallopAlpha(Math.round(width * 2)),
    }),
    { cast: false },
  );
  valance.position.set(0, marqueeY - 0.90, -0.30);
  g.add(valance);

  const lights = createLampString(width, 14, { palette: 'multi' });
  lights.position.set(0, height - 0.15, -0.40);
  g.add(lights);
  g.userData.lights = lights;

  return g;
}

/** A stoppered sweet jar for the prize shelves. */
function prizeJar(color) {
  const g = new THREE.Group();
  const glass = mat(0xdcefe6, { rough: 0.08, transparent: true, opacity: 0.38 });
  const body = mesh(
    geo('jar-body', () => new THREE.CylinderGeometry(0.085, 0.078, 0.20, 12)),
    glass,
    { receive: false },
  );
  body.position.y = 0.10;
  g.add(body);
  const sweets = mesh(
    geo('jar-sweets', () => new THREE.CylinderGeometry(0.072, 0.068, 0.14, 12)),
    mat(color, { rough: 0.5 }),
    { cast: false },
  );
  sweets.position.y = 0.078;
  g.add(sweets);
  const lid = mesh(
    geo('jar-lid', () => new THREE.SphereGeometry(0.055, 10, 7)),
    mat(ART.gold, { rough: 0.4, metal: 0.5 }),
  );
  lid.scale.y = 0.7;
  lid.position.y = 0.215;
  g.add(lid);
  return g;
}

/**
 * The prize desk (GDD 7.5) — grand counter, columned prize wall, marquee.
 * @returns {THREE.Group} with `userData.interactPoint` and `userData.chase(t)`.
 */
export function createPrizeDesk() {
  const g = new THREE.Group();
  g.name = 'prize-desk';

  const timber = mat(0xffffff, { rough: 0.74, map: woodGrain(ART.wood) });

  const desk = mesh(slab(4.2, 1.05, 1.0, 0.04), mat(ART.plum, { rough: 0.82 }));
  desk.position.y = 0.525;
  g.add(desk);

  // Panelled front — the counter faces the player at close range.
  for (let i = 0; i < 5; i++) {
    const p = mesh(slab(0.66, 0.62, 0.05, 0.015), mat(ART.plumLight, { rough: 0.82 }));
    p.position.set(-1.68 + i * 0.84, 0.55, -0.51);
    g.add(p);
    const boss = mesh(
      geo('desk-boss', () => new THREE.SphereGeometry(0.045, 9, 7)),
      mat(ART.gold, { rough: 0.35, metal: 0.65 }),
    );
    boss.position.set(-1.68 + i * 0.84, 0.55, -0.55);
    g.add(boss);
  }

  const top = mesh(slab(4.44, 0.1, 1.2, 0.03), timber);
  top.position.y = 1.09;
  g.add(top);

  const topTrim = mesh(slab(4.5, 0.06, 1.26, 0.02), mat(ART.gold, { rough: 0.4, metal: 0.55 }));
  topTrim.position.y = 1.02;
  g.add(topTrim);

  // ── Prize wall ────────────────────────────────────────────────────────────
  const backWall = mesh(
    new THREE.BoxGeometry(4.6, 3.0, 0.15),
    mat(0xffffff, { rough: 0.85, map: starburst(ART.gold, ART.red, 24) }),
  );
  backWall.position.set(0, 1.5, 1.0);
  g.add(backWall);

  for (const s of [-1, 1]) {
    const column = mesh(
      geo('desk-column', () => new THREE.CylinderGeometry(0.13, 0.13, 3.0, 12)),
      mat(0xffffff, { rough: 0.6, map: stripes(ART.teal, ART.cream, 10) }),
    );
    column.position.set(2.2 * s, 1.5, 0.86);
    g.add(column);

    const cap = mesh(slab(0.34, 0.14, 0.34, 0.03), mat(ART.gold, { rough: 0.4, metal: 0.5 }));
    cap.position.set(2.2 * s, 3.05, 0.86);
    g.add(cap);
  }

  const rnd = rngFor('prize-desk');
  const jarColors = [ART.red, ART.teal, ART.gold, ART.plumLight];
  for (let row = 0; row < 3; row++) {
    const shelf = mesh(slab(4.2, 0.07, 0.34, 0.015), timber);
    shelf.position.set(0, 0.9 + row * 0.6, 0.82);
    g.add(shelf);

    const lip = mesh(slab(4.2, 0.05, 0.03, 0.008), mat(ART.gold, { rough: 0.45, metal: 0.4 }));
    lip.position.set(0, 0.965 + row * 0.6, 0.655);
    g.add(lip);

    // Alternate plush rows with jars so the wall has some rhythm.
    for (let i = 0; i < 5; i++) {
      const x = -1.7 + i * 0.85;
      const y = 0.945 + row * 0.6;
      if ((row + i) % 3 === 1) {
        const jar = prizeJar(jarColors[(row + i) % jarColors.length]);
        jar.position.set(x, y, 0.82);
        jar.scale.setScalar(0.9);
        g.add(jar);
      } else {
        const p = createPlush(i % 3 === 0 ? 'large' : 'small');
        p.position.set(x, y, 0.82);
        p.rotation.y = Math.PI + (rnd() - 0.5) * 0.6;
        p.scale.setScalar(i % 3 === 0 ? 0.62 : 0.8);
        g.add(p);
      }
    }
  }

  // ── PRIZES marquee with chase bulbs ───────────────────────────────────────
  // Plum rather than plumDeep, and dressed with a burst and bulb rails like
  // the booth fascia. Unlit deep plum at this size read as a black hole
  // punched in the top of the prize wall.
  const marquee = mesh(slab(3.6, 0.9, 0.24, 0.03), mat(ART.plum, { rough: 0.82 }));
  marquee.position.set(0, 2.62, 0.86);
  g.add(marquee);

  const marqueeBurst = mesh(
    geo('desk-marquee-burst', () => new THREE.PlaneGeometry(3.5, 0.78)),
    mat(0xffffff, {
      flat: true, map: starburst(ART.gold, ART.plum, 20),
      transparent: true, opacity: 0.34,
    }),
    { cast: false, receive: false },
  );
  marqueeBurst.position.set(0, 2.62, 0.737);
  marqueeBurst.rotation.y = Math.PI;
  g.add(marqueeBurst);

  for (const dy of [0.38, -0.38]) {
    const rail = mesh(
      geo('desk-marquee-rail', () => new THREE.PlaneGeometry(3.6, 0.11)),
      mat(0xffffff, { flat: true, map: bulbBand(14) }),
      { cast: false, receive: false },
    );
    rail.position.set(0, 2.62 + dy, 0.736);
    rail.rotation.y = Math.PI;
    g.add(rail);
  }

  const sign = createSign('PRIZES', {
    w: 3.0, h: 0.62, bg: 'transparent',
    color: `#${ART.gold.toString(16)}`, border: null, size: 110,
  });
  sign.position.set(0, 2.62, 0.735);
  sign.rotation.y = Math.PI;
  g.add(sign);

  const chaseBulbs = [];
  const n = 14;
  for (let i = 0; i < n; i++) {
    const b = new THREE.Mesh(
      geo('chase-bulb', () => new THREE.SphereGeometry(0.055, 7, 5)),
      new THREE.MeshBasicMaterial({ color: ART.bulbWarm, transparent: true, opacity: 0.9 }),
    );
    const t = i / (n - 1);
    b.position.set(-1.75 + t * 3.5, 2.62 + (i % 2 ? 0.40 : -0.40), 0.72);
    g.add(b);
    chaseBulbs.push(b);
  }

  /**
   * Run a chase around the marquee bulbs.
   * @param {number} elapsed
   */
  g.userData.chase = (elapsed) => {
    for (let i = 0; i < chaseBulbs.length; i++) {
      const k = 0.35 + 0.65 * Math.max(0, Math.sin(elapsed * 4 - i * 0.55));
      chaseBulbs[i].material.opacity = k;
      chaseBulbs[i].scale.setScalar(0.8 + k * 0.4);
    }
  };

  const strip = buntingStrip([ART.red, ART.cream, ART.teal, ART.gold]);
  const swag = mesh(
    geo('desk-swag', () => {
      const p = new THREE.PlaneGeometry(4.4, 0.34, 20, 1);
      const pos = p.attributes.position;
      for (let i = 0; i < pos.count; i++) {
        const t = pos.getX(i) / 4.4 + 0.5;
        pos.setY(i, pos.getY(i) - Math.sin(t * Math.PI) * 0.26);
      }
      p.computeVertexNormals();
      return p;
    }),
    mat(0xffffff, {
      flat: true, map: strip.map, alphaMap: strip.alpha,
      side: THREE.DoubleSide, transparent: true,
    }),
    { cast: false, receive: false },
  );
  swag.position.set(0, 3.15, 0.70);
  g.add(swag);

  g.userData.interactPoint = new THREE.Vector3(0, 0, -1.6);
  return g;
}

/**
 * A leaderboard board (GDD 7.6, 10.2) in a bulb-lit carnival frame.
 * @param {string} [title] @param {string[]} [rows]
 * @returns {THREE.Group} with `userData.setRows(rows)`.
 */
export function createLeaderboardBoard(title = 'WEEKLY LEADERS', rows = []) {
  const g = new THREE.Group();
  g.name = 'leaderboard';

  const frame = mesh(slab(2.42, 3.1, 0.16, 0.03), mat(ART.plumDeep, { rough: 0.82 }));
  frame.position.y = 1.9;
  g.add(frame);

  const bezel = mesh(slab(2.56, 3.24, 0.10, 0.03), mat(ART.gold, { rough: 0.42, metal: 0.45 }));
  bezel.position.set(0, 1.9, 0.02);
  g.add(bezel);

  // Bulb rails down both sides of the frame.
  for (const s of [-1, 1]) {
    const rail = mesh(
      new THREE.PlaneGeometry(0.16, 3.24),
      mat(0xffffff, { flat: true, map: bulbBand(10) }),
      { cast: false, receive: false },
    );
    rail.position.set(1.20 * s, 1.9, -0.087);
    rail.rotation.y = Math.PI;
    rail.rotation.z = Math.PI / 2;
    g.add(rail);
  }

  const crest = mesh(
    geo('lb-crest', () => new THREE.ConeGeometry(0.42, 0.42, 3)),
    mat(ART.red, { rough: 0.6 }),
  );
  crest.position.set(0, 3.62, 0);
  crest.rotation.y = Math.PI / 2;
  g.add(crest);

  const star = finial(0.13, ART.gold);
  star.position.set(0, 3.86, 0);
  g.add(star);

  for (const s of [-1, 1]) {
    const leg = mesh(
      geo('lb-leg', () => new THREE.CylinderGeometry(0.07, 0.09, 0.8, 8)),
      mat(ART.iron, { rough: 0.45, metal: 0.6 }),
    );
    leg.position.set(0.8 * s, 0.4, 0);
    g.add(leg);

    const foot = mesh(
      geo('lb-foot', () => new THREE.CylinderGeometry(0.16, 0.19, 0.08, 10)),
      mat(ART.iron, { rough: 0.6, metal: 0.5 }),
    );
    foot.position.set(0.8 * s, 0.04, 0);
    g.add(foot);
  }

  const lines = [title, ...(rows.length ? rows : ['1. —', '2. —', '3. —', '4. —', '5. —'])];

  // A readable face on BOTH sides of the frame. The hub yaws these boards to
  // flank the walkway, and whichever way it turns them one side ends up
  // presenting the frame's back — which renders as a black slab at the
  // entrance, the first thing a player sees. Two faces makes the placement
  // question moot without hub.js having to care.
  const faces = [-1, 1].map((side) => {
    const face = createSign('', {
      w: 2.0, h: 2.6, lines,
      bg: `#${ART.plumDeep.toString(16)}`,
      color: `#${ART.gold.toString(16)}`,
      size: 84, frame: false,
    });
    face.position.set(0, 1.9, 0.085 * side);
    if (side < 0) face.rotation.y = Math.PI;
    face.name = side < 0 ? 'board-face' : 'board-face-back';
    g.add(face);
    return face;
  });

  g.userData.setRows = (newRows) => {
    for (const f of faces) f.userData.setText([title, ...newRows]);
  };
  return g;
}

/** The swirl inside the personal-space portal. */
function portalSwirl(color) {
  const key = typeof color === 'number' ? color : 0x9b5de5;
  return paint(`portal-swirl-${key}`, 512, 512, (ctx, w, h) => {
    const cx = w / 2, cy = h / 2;
    ctx.fillStyle = `#${shade(key, 0.55).toString(16).padStart(6, '0')}`;
    ctx.fillRect(0, 0, w, h);
    ctx.lineCap = 'round';
    for (let arm = 0; arm < 5; arm++) {
      ctx.beginPath();
      for (let i = 0; i <= 60; i++) {
        const t = i / 60;
        const a = arm * (Math.PI * 2 / 5) + t * 3.4;
        const r = t * w * 0.47;
        const fn = i ? 'lineTo' : 'moveTo';
        ctx[fn](cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      ctx.strokeStyle = `#${tint(key, 0.45).toString(16).padStart(6, '0')}`;
      ctx.lineWidth = 26;
      ctx.globalAlpha = 0.55;
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    const grad = ctx.createRadialGradient(cx, cy, 4, cx, cy, w * 0.5);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.35, `#${tint(key, 0.30).toString(16).padStart(6, '0')}`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.globalAlpha = 0.7;
    ctx.beginPath(); ctx.arc(cx, cy, w * 0.5, 0, Math.PI * 2); ctx.fill();
  });
}

/**
 * A glowing portal disc (personal space entrance).
 * @param {number} [color] @param {number} [radius]
 * @returns {THREE.Group} with `userData.pulse(elapsed)`.
 */
export function createPortal(color = 0x9b5de5, radius = 1.1) {
  const g = new THREE.Group();
  g.name = 'portal';
  const centreY = radius + 0.15;

  const frame = mesh(
    new THREE.TorusGeometry(radius, 0.12, 12, 32),
    mat(ART.gold, { rough: 0.3, metal: 0.8 }),
  );
  frame.position.y = centreY;
  g.add(frame);

  // Studs around the ring.
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const stud = mesh(
      geo('portal-stud', () => new THREE.SphereGeometry(0.055, 8, 6)),
      mat(ART.gold, { rough: 0.25, metal: 0.85 }),
      { receive: false },
    );
    stud.position.set(Math.cos(a) * radius, centreY + Math.sin(a) * radius, 0.10);
    g.add(stud);
  }

  const swirl = new THREE.Mesh(
    new THREE.CircleGeometry(radius, 32),
    new THREE.MeshBasicMaterial({
      map: portalSwirl(color), color,
      transparent: true, opacity: 0.62, side: THREE.DoubleSide, depthWrite: false,
    }),
  );
  swirl.position.y = centreY;
  swirl.name = 'portal-swirl';
  g.add(swirl);

  // Orbiting motes. Each keeps its own material so they can twinkle apart.
  const motes = [];
  const rnd = rngFor('portal');
  for (let i = 0; i < 14; i++) {
    const m = new THREE.Mesh(
      geo('portal-mote', () => new THREE.SphereGeometry(0.036, 6, 5)),
      new THREE.MeshBasicMaterial({ color: 0xfff3d0, transparent: true, opacity: 0.8 }),
    );
    m.userData.a = rnd() * Math.PI * 2;
    m.userData.r = radius * (0.35 + rnd() * 0.75);
    m.userData.speed = 0.4 + rnd() * 0.9;
    g.add(m);
    motes.push(m);
  }

  const plinth = mesh(
    geo('portal-plinth', () => new THREE.CylinderGeometry(radius * 0.85, radius * 1.05, 0.16, 16)),
    mat(ART.plum, { rough: 0.85 }),
  );
  plinth.position.y = 0.08;
  g.add(plinth);

  /**
   * Breathe the portal and drift the motes.
   * @param {number} elapsed
   */
  g.userData.pulse = (elapsed) => {
    swirl.material.opacity = 0.50 + 0.16 * Math.sin(elapsed * 2.4);
    swirl.rotation.z = elapsed * 0.35;
    for (const m of motes) {
      const a = m.userData.a + elapsed * m.userData.speed;
      m.position.set(
        Math.cos(a) * m.userData.r,
        centreY + Math.sin(a) * m.userData.r,
        0.05 + Math.sin(elapsed + m.userData.a) * 0.06,
      );
      m.material.opacity = 0.45 + 0.45 * Math.sin(elapsed * 3 + m.userData.a * 2);
    }
  };
  g.userData.swirl = swirl;
  return g;
}
